import React, { useState, useRef, useCallback } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Box, Divider } from "@mui/material";
import usePlanDetails from "app/hooks/usePlanDetails";
import useJumboAuth from "@jumbo/hooks/useJumboAuth";
import { ROUTES } from "app/utils/constants/routeConstants";
import JumboCardQuick from "@jumbo/components/JumboCardQuick";
import ContentHeader from "components/ContentHeader/ContentHeader";
import CourseOverview from "./CourseOverview";
import InstructorDetails from "./InstructorDetails";
import CourseContent from "./CourseContent";
import useAdmin from "app/routes/middleware/Admin/useAdmin";
import LoadingView from "components/LoadingView";
import ErrorView from "components/ErrorView";
import useMetaTitle from "app/hooks/useMetaTitle";
import { getSocialIcon } from "components/SocialMediaIcons";

const PricingDetails = () => {
  const { id } = useParams();
  const { plan, loading, error } = usePlanDetails(id);
  const { authUser } = useJumboAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isAdmin = useAdmin();

  const isLoggedIn = !!authUser;

  // Video player state
  const [loadedVideos, setLoadedVideos] = useState({});
  const [activeVideo, setActiveVideo] = useState(null);
  const videoPlayerRef = useRef(null);

  const handleUpdate = useCallback(() => {
    navigate(`${ROUTES.ADMIN_PLANS_UPDATE}`.replace(":id", id));
  }, [navigate, id]);

  const handleAdd = useCallback(() => {
    navigate(`${ROUTES.ADMIN_PLANS_ADD}`);
  }, [navigate]);

  // Handle back navigation
  const handleBack = useCallback(() => {
    setTimeout(() => {
      if (window.location.pathname === location.pathname) {
        navigate(ROUTES.UPGRADE);
      }
    }, 500);
  }, [navigate, location.pathname]);

  const handleAction = useCallback(
    ({ id: actionId }) => {
      const actions = {
        back: handleBack,
        update: handleUpdate,
        add: handleAdd,
      };
      actions[actionId]?.();
    },
    [handleBack, handleUpdate, handleAdd]
  );

  useMetaTitle({
    title: plan?.name,
  });

  const getVideoThumbnail = (video) => {
    if (video.provider === "YOUTUBE") {
      const videoId = video.videoLink.includes("youtu.be/")
        ? video.videoLink.split("youtu.be/")[1]?.split("?")[0]
        : video.videoLink.split("v=")[1]?.split("&")[0];
      return videoId
        ? `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`
        : null;
    }
    return null;
  };

  const getEmbedUrl = (videoLink, provider) => {
    if (provider === "YOUTUBE") {
      const videoId = videoLink.includes("youtu.be/")
        ? videoLink.split("youtu.be/")[1]?.split("?")[0]
        : videoLink.split("v=")[1]?.split("&")[0];
      return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
    }
    return null;
  };

  if (loading) {
    return <LoadingView message="Loading plan details..." />;
  }

  if (error) {
    return (
      <ErrorView error={error} onBack={handleBack} backButtonLabel="Back" />
    );
  }

  if (!plan) {
    return (
      <ErrorView
        error="Plan not found"
        onBack={handleBack}
        backButtonLabel="Back"
      />
    );
  }

  return (
    <JumboCardQuick
      title={
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            flexDirection: "row",
          }}
        >
          <ContentHeader
            isAdmin={isAdmin}
            onAction={handleAction}
            allowedActions={isAdmin ? ["add", "update", "back"] : ["back"]}
            actionLabels={{
              add: "Add Plan",
              update: "Update Plan",
              back: "Back",
            }}
          />
        </Box>
      }
      headerSx={{
        borderBottom: 1,
        borderColor: "divider",
        "& .MuiCardHeader-action": { my: -0.75 },
        height: "60px",
        display: "flex",
        alignItems: "center",
        px: 2,
      }}
      wrapperSx={{
        p: 0,
        flexGrow: 1,
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        "& .MuiCardContent-root:last-child": {
          paddingBottom: 0,
        },
      }}
    >
      <Box sx={{ p: 3 }}>
        <CourseOverview
          plan={plan}
          isLoggedIn={isLoggedIn}
          authUser={authUser}
          navigate={navigate}
          location={location}
        />
        <Divider sx={{ my: 3 }} />
        <InstructorDetails
          instructors={plan.instructorDetails}
          getSocialIcon={getSocialIcon}
        />
        <Divider sx={{ my: 3 }} />
        <CourseContent
          contentDetails={plan.contentDetails}
          loadedVideos={loadedVideos}
          setLoadedVideos={setLoadedVideos}
          activeVideo={activeVideo}
          setActiveVideo={setActiveVideo}
          videoPlayerRef={videoPlayerRef}
          getVideoThumbnail={getVideoThumbnail}
          getEmbedUrl={getEmbedUrl}
          isLoggedIn={isLoggedIn}
          authUser={authUser}
          navigate={navigate}
          location={location}
          plan={plan}
        />
      </Box>
    </JumboCardQuick>
  );
};

export default PricingDetails;
